import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';
import { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Button } from './Button';
import { Icon } from './Icon';
import { ICONS } from '@/constants/icons';
import { CancelButton } from '@/components/ui/CancelButton';

/**
 * PromptDialog Component
 *
 * A visually branded input prompt dialog using Radix Dialog
 * Replaces browser native prompt() with better UX
 *
 * Features:
 * - Gradient header based on variant
 * - Animated icon with subtle bounce
 * - Backdrop blur effect
 * - Smooth open/close animations
 * - Support for text, number, and textarea inputs
 * - Quick select buttons for common values
 * - Mobile responsive (90vw max)
 * - WCAG 2.1 AA compliant
 */

interface QuickSelectOption {
  label: string;
  value: string | number;
}

interface PromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Input label */
  label?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Default value for the input */
  defaultValue?: string;
  /** Input type: text, number, or textarea */
  inputType?: 'text' | 'number' | 'textarea';
  /** Min value for number inputs */
  min?: number;
  /** Max value for number inputs */
  max?: number;
  /** Whether the input is required */
  required?: boolean;
  /** Quick select options */
  quickSelect?: QuickSelectOption[];
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'info' | 'success';
  onConfirm: (value: string) => void | Promise<void>;
  onCancel?: () => void;
  /** Show loading state on confirm button */
  loading?: boolean;
  /** Validation function - return error message or undefined if valid */
  validate?: (value: string) => string | undefined;
}

export const PromptDialog = ({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder = '',
  defaultValue = '',
  inputType = 'text',
  min,
  max,
  required = false,
  quickSelect,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  loading = false,
  validate,
}: PromptDialogProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Reset value when dialog opens
  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError(undefined);
      // Focus input after animation
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [open, defaultValue]);

  const handleConfirm = async () => {
    // Validate required
    if (required && !value.trim()) {
      setError('This field is required');
      return;
    }

    // Validate number range
    if (inputType === 'number') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        setError('Please enter a valid number');
        return;
      }
      if (min !== undefined && numValue < min) {
        setError(`Value must be at least ${min}`);
        return;
      }
      if (max !== undefined && numValue > max) {
        setError(`Value must be at most ${max}`);
        return;
      }
    }

    // Custom validation
    if (validate) {
      const validationError = validate(value);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setError(undefined);
    await onConfirm(value);
    if (!loading) {
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputType !== 'textarea') {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Configuration for each variant
  const variantConfig = {
    default: {
      icon: ICONS.EDIT,
      headerGradient: 'bg-gradient-mint',
      iconBg: 'bg-white/20',
      textColor: 'text-gray-900',
    },
    info: {
      icon: ICONS.INFO,
      headerGradient: 'bg-gradient-mint',
      iconBg: 'bg-white/20',
      textColor: 'text-gray-900',
    },
    success: {
      icon: ICONS.CHECK,
      headerGradient: 'bg-gradient-mint',
      iconBg: 'bg-white/20',
      textColor: 'text-gray-900',
    },
  };

  const config = variantConfig[variant];
  const stackZ = getStackZIndex(useModalStackDepth(open));

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Backdrop with blur effect */}
        <Dialog.Overlay
          style={{ zIndex: stackZ.backdrop }}
          className={cn(
            'fixed inset-0',
            'bg-black/50 backdrop-blur-sm',
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out',
            'duration-200'
          )}
        />

        {/* Dialog Content */}
        <Dialog.Content
          style={{ zIndex: stackZ.content }}
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[90vw] max-w-md',
            'max-h-[90vh]',
            'bg-white rounded-2xl shadow-2xl',
            'overflow-hidden',
            'focus:outline-none',
            'flex flex-col',
            'data-[state=open]:animate-zoom-in',
            'data-[state=closed]:animate-zoom-out'
          )}
        >
          {/* Header - Gradient */}
          <div
            className={cn(
              'flex-shrink-0',
              'px-6 pt-8 pb-6',
              config.headerGradient,
              'relative overflow-hidden'
            )}
          >
            {/* Icon in frosted glass circle */}
            <div className="relative z-10 flex flex-col items-center">
              <div
                className={cn(
                  'w-16 h-16',
                  config.iconBg,
                  'backdrop-blur-sm',
                  'rounded-full',
                  'flex items-center justify-center',
                  'mb-4',
                  'shadow-lg shadow-black/10',
                  'border border-white/20',
                  'animate-bounce-subtle'
                )}
              >
                <Icon
                  name={config.icon}
                  size="lg"
                  className={cn("drop-shadow-sm", config.textColor === 'text-gray-900' ? 'text-secondary-500' : 'text-white')}
                />
              </div>

              {/* Title */}
              <Dialog.Title className={cn("text-base sm:text-lg md:text-xl font-bold text-center drop-shadow-sm", config.textColor)}>
                {title}
              </Dialog.Title>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {/* Description - always required for accessibility */}
            {description ? (
              <Dialog.Description className="text-sm text-gray-600 text-center leading-relaxed">
                {description}
              </Dialog.Description>
            ) : (
              <VisuallyHidden asChild>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <Dialog.Description>{title} input dialog</Dialog.Description>
              </VisuallyHidden>
            )}

            {/* Input Field */}
            <div>
              {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {label} {required && <span className="text-red-500">*</span>}
                </label>
              )}

              {inputType === 'textarea' ? (
                <textarea
                  ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setError(undefined);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  disabled={loading}
                  rows={3}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl border-2 transition-all duration-200',
                    'text-gray-900 placeholder-gray-400',
                    'focus:outline-none focus:ring-0',
                    error
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-primary-500',
                    'disabled:bg-gray-50 disabled:text-gray-500'
                  )}
                />
              ) : (
                <input
                  ref={inputRef as React.RefObject<HTMLInputElement>}
                  type={inputType}
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setError(undefined);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  disabled={loading}
                  min={min}
                  max={max}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl border-2 transition-all duration-200',
                    'text-gray-900 placeholder-gray-400',
                    'focus:outline-none focus:ring-0',
                    error
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-primary-500',
                    'disabled:bg-gray-50 disabled:text-gray-500'
                  )}
                />
              )}

              {/* Error message */}
              {error && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <Icon name={ICONS.WARNING} size="sm" />
                  {error}
                </p>
              )}
            </div>

            {/* Quick Select */}
            {quickSelect && quickSelect.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">
                  {t('prompt_quick_select')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {quickSelect.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setValue(String(option.value));
                        setError(undefined);
                      }}
                      disabled={loading}
                      className={cn(
                        'px-3 py-1.5 text-sm rounded-lg border-2 transition-all duration-200',
                        'font-medium',
                        String(option.value) === value
                          ? 'bg-primary-500 text-primary-contrast border-primary-500'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer - Action Buttons */}
          <div className="flex-shrink-0 px-6 pb-6 flex justify-end gap-1.5 sm:gap-2">
            <CancelButton
              onClick={handleCancel}
              disabled={loading}
              tooltip={cancelText}
            />
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={loading}
              className="transition-all duration-200"
            >
              {confirmText}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PromptDialog;
